import { useState } from "react";
import { api } from "../../../data";
import { Dialog } from "../../../components/ui";
import { fehlerText } from "../../../lib/fehler";
import { t } from "../../../lib/sprache";
import { requestCancellation } from "./data";
import type { LifecycleStatus } from "./model";

/**
 * Konto löschen, in jedem Zustand (0205/0207, Inhaber 13.10.2026: „Man muss sein Konto natürlich immer löschen können!“).
 *  - Testkonto (nie bezahlt): sofort und vollständig, auf Wunsch samt Anmeldung. Ein hinterlegtes Probeabo beendet die
 *    Serverfunktion „konto-dienste“ vorher, es wird nichts abgebucht; danach kommt eine Bestätigung per Mail.
 *  - Konto mit Abo oder Belegen: ein Schritt merkt Kündigung und Löschung vor (KontoLoeschen unten); gelöscht wird nach dem
 *    Ende der Laufzeit mit Ankündigung, bis dahin lässt sich der Wunsch zurückziehen.
 * Steht im Zahlfenster, im Nur-Lesen-Hinweis und unter Einstellungen · Abo & Tarif.
 */
export function TestkontoLoeschen({ orgId, label }: { orgId: string; label?: string }) {
  const [offen, setOffen] = useState(false);
  const [ja, setJa] = useState(false);
  const [mitAnmeldung, setMitAnmeldung] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const titel = label ?? t("Testkonto löschen", "Delete trial account");
  const loeschen = async () => {
    setBusy(true);
    setError(null);
    try {
      if (api.kontoDienste) await api.kontoDienste({ action: "testkonto_loeschen", org: orgId, mit_anmeldung: mitAnmeldung });
      else await api.rpc("trial_account_delete", { p_org: orgId, p_confirm: true, p_abo_beendet: false, p_mit_anmeldung: mitAnmeldung });
      if (mitAnmeldung) await api.signOut({ everywhere: true }).catch(() => undefined);
      window.location.assign("/");
    } catch (e) {
      setError(fehlerText(e));
      setBusy(false);
    }
  };
  return (
    <>
      <button type="button" className="quiet" onClick={() => { setOffen(true); setJa(false); setError(null); }}>{titel}</button>
      <Dialog open={offen} onClose={() => !busy && setOffen(false)} title={titel}>
        <div className="stack">
          <p>{t("Das Konto wird sofort und vollständig gelöscht: alle Creator, Marken, Kooperationen, Dateien und Einstellungen. Das lässt sich nicht rückgängig machen. Eine hinterlegte Zahlungsmethode wird nicht belastet, das Probeabo endet damit. Du bekommst eine Bestätigung per Mail.",
            "The account is deleted immediately and completely: all creators, brands, deals, files and settings. This cannot be undone. A stored payment method is not charged; the trial subscription ends with this. You receive a confirmation by email.")}</p>
          <label className="row tk-check"><input type="checkbox" checked={ja} onChange={(e) => setJa(e.target.checked)} /><span>{t("Ja, alle Daten dieses Kontos endgültig löschen.", "Yes, permanently delete all data of this account.")}</span></label>
          <label className="row tk-check"><input type="checkbox" checked={mitAnmeldung} onChange={(e) => setMitAnmeldung(e.target.checked)} /><span>{t("Auch meine Anmeldung (E-Mail-Zugang) löschen.", "Also delete my sign-in (email access).")}</span></label>
          {error && <p className="tk-error" role="alert">{error}</p>}
          <div className="row">
            <button type="button" className="danger" disabled={!ja || busy} aria-busy={busy} onClick={() => void loeschen()}>{busy ? t("Wird gelöscht …", "Deleting …") : t("Endgültig löschen", "Delete permanently")}</button>
            <button type="button" disabled={busy} onClick={() => setOffen(false)}>{t("Abbrechen", "Cancel")}</button>
          </div>
        </div>
      </Dialog>
    </>
  );
}

/** „Konto löschen“ für jeden Zustand: wählt selbst zwischen sofort (Testkonto) und vorgemerkt (Abo, Belege). */
export function KontoLoeschen({ orgId, s, fmt, onDone, onKuendigungsweg }: {
  orgId: string; s: LifecycleStatus; fmt: (iso: string) => string; onDone: () => Promise<unknown> | void;
  /** Inhaber 21.10.2026: läuft noch ein Abo, führt „Konto löschen“ durch den Kündigungsweg (fünf Schritte) und merkt erst danach die Löschung vor */
  onKuendigungsweg?: () => void;
}) {
  const [offen, setOffen] = useState(false);
  const [ja, setJa] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  if (!s.can_delete_account) return null;
  // 0425 (S-2): auch abgelaufen oder gesperrt sofort löschbar, so beschriftet wie auf der Sperrseite
  if (s.can_delete_trial) return <TestkontoLoeschen orgId={orgId} label={s.status_read_only ? t("Testkonto jetzt löschen", "Delete trial account now") : t("Konto löschen", "Delete account")} />;
  if (s.can_cancel && !s.early_purge_requested_at && onKuendigungsweg) {
    return <button type="button" className="quiet" onClick={onKuendigungsweg}>{t("Konto löschen", "Delete account")}</button>;
  }
  const zurueck = async () => {
    setBusy(true);
    setError(null);
    try {
      await api.rpc("org_purge_early_withdraw", { p_org: orgId });
      await onDone();
    } catch (e) {
      setError(fehlerText(e));
    } finally {
      setBusy(false);
    }
  };
  if (s.early_purge_requested_at) {
    return (
      <div className="stack">
        <p role="status">{t(`Löschung vorgemerkt am ${fmt(s.early_purge_requested_at)}`, `Deletion scheduled on ${fmt(s.early_purge_requested_at)}`)}{s.plan?.next_at ? t(`; gelöscht wird frühestens am ${fmt(s.plan.next_at)}, mit Ankündigung per Mail.`, `; data is deleted on ${fmt(s.plan.next_at)} at the earliest, with notice by email.`) : "."}</p>
        {s.can_withdraw_early_purge && <div className="row"><button type="button" disabled={busy} onClick={() => void zurueck()}>{t("Löschwunsch zurückziehen", "Withdraw deletion request")}</button></div>}
        {error && <p className="tk-error" role="alert">{error}</p>}
      </div>
    );
  }
  const vormerken = async () => {
    setBusy(true);
    setError(null);
    try {
      // Läuft noch ein Abo oder eine Testphase mit Zahlungsmethode: erst beenden (derselbe Weg wie „Kündigung vormerken“)
      if (s.can_cancel) await requestCancellation(api, orgId, t("Konto löschen", "Delete account"));
      await api.rpc("org_delete_request", { p_org: orgId, p_confirm: true });
      await onDone();
      setOffen(false);
    } catch (e) {
      setError(fehlerText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
      <button type="button" className="quiet" onClick={() => { setOffen(true); setJa(false); setError(null); }}>{t("Konto löschen", "Delete account")}</button>
      <Dialog open={offen} onClose={() => !busy && setOffen(false)} title={t("Konto löschen", "Delete account")}>
        <div className="stack">
          <p>{s.can_cancel && s.status === "trial"
            // K-217: In der Testphase gilt die Kündigung sofort (0251), nicht zum Ende einer Laufzeit
            ? t("Damit endet deine Testphase sofort, und die Löschung aller Daten wird vorgemerkt. Das Portal ist ab sofort gesperrt; nach einer Ankündigung per Mail werden alle Daten endgültig gelöscht, auch Rechnungen und Verträge. Es wird nichts abgebucht.",
                "This ends your trial immediately and schedules the deletion of all data. The portal is locked from now on; after a notice by email all data is permanently deleted, including invoices and contracts. Nothing is charged.")
            : s.can_cancel
            ? t("Damit beendest du dein Abo zum Ende der Laufzeit und merkst die Löschung aller Daten vor. Bis zum Ende der Laufzeit arbeitet ihr wie gewohnt; danach ist das Portal gesperrt, und alle Daten werden nach einer Ankündigung per Mail endgültig gelöscht. Es wird nichts weiter abgebucht.",
                "This ends your subscription at the end of the term and schedules the deletion of all data. Until the end of the term you work as usual; afterwards the portal is locked, and all data is permanently deleted after a notice by email. Nothing further is charged.")
            : t("Damit merkst du die Löschung aller Daten vor. Sie werden nach einer Ankündigung per Mail endgültig gelöscht.",
                "This schedules the deletion of all data. It is permanently deleted after a notice by email.")}</p>
          <p>{t("Bitte lade vorher unter Einstellungen · Export alles herunter, was du aufbewahren musst (Rechnungen, Verträge). Bis zum Löschtag kannst du den Wunsch hier zurückziehen.",
            "Please download everything you must retain (invoices, contracts) under Settings · Export beforehand. Until the deletion date you can withdraw the request here.")}</p>
          <label className="row tk-check"><input type="checkbox" checked={ja} onChange={(e) => setJa(e.target.checked)} /><span>{t("Ich habe meine Daten exportiert und sorge selbst für die gesetzliche Aufbewahrung. Das Konto soll gelöscht werden.", "I have exported my data and take care of statutory retention myself. The account shall be deleted.")}</span></label>
          {error && <p className="tk-error" role="alert">{error}</p>}
          <div className="row">
            <button type="button" className="danger" disabled={!ja || busy} aria-busy={busy} onClick={() => void vormerken()}>{busy ? t("Wird vorgemerkt …", "Scheduling …") : t("Löschung vormerken", "Schedule deletion")}</button>
            <button type="button" disabled={busy} onClick={() => setOffen(false)}>{t("Abbrechen", "Cancel")}</button>
          </div>
        </div>
      </Dialog>
    </>
  );
}
